import * as React from 'react';
import { ANIMATE_OUT_SPEED } from 'ui/constants';

/**
 * This interface is used by the wrapped component to know what to expect from this higher-order component.
 */
export interface OpenStateInjectedProps {
  isOpen: boolean;
  isClosing: boolean;
  open: () => void;
  close: (onClosedCallback?: () => void) => void;
}

export interface OpenStateState extends React.ComponentState {
  isOpen: boolean;
  isClosing: boolean;
}

export const withOpenState = (animated: boolean = false) =>
<P extends {}>(
  WrappedComponent: (React.ComponentClass<P & OpenStateInjectedProps> | React.FC<P & OpenStateInjectedProps>)
): React.ComponentClass<P> => {
  return class extends React.Component<P, OpenStateState> {

    constructor(props: P) {
      super(props);

      this.state = this.createStateObject(false);
    }

    private createStateObject(isOpen: boolean, isClosing: boolean = false) {
      return {
        isOpen,
        isClosing
      } as Readonly<OpenStateState>;
    }

    private open = () => {      
      if (this.state.isOpen) {
        return;
      }

      this.setState(this.createStateObject(true));
    };

    private close = (onClosedCallback: () => void) => {
      if (!this.state.isOpen) {
        return;
      }

      this.setState(this.createStateObject(true, true));

      const setClosedState = () =>
        this.setState(
          this.createStateObject(false, false), 
          onClosedCallback
        );

      if(animated) {
        setTimeout(setClosedState, ANIMATE_OUT_SPEED);
      } else {
        setClosedState();
      }
    };

    public render() {
      return <WrappedComponent
        {...this.props} {...{ isOpen: this.state.isOpen, close: this.close, open: this.open} as any}
      />;
    }
  };
};
